<template>
  <div class="app" :class="centerLayoutClass">
    <!-- <Debug /> -->
    <SplashScreen />
    <NFTMainBackground v-if="orderStore.isNFTMode" />
    <MainBackground v-else-if="!isWidget" />
    <MainSidebar v-if="!isWidget" />
    <MainBanner
      v-if="showBanner"
      :banner="getNotificationBanner"
      @onButtonClick="notificationStore.dismissBanner(route.name)"
    />
    <div class="content">
      <MainContent>
        <RouterView v-slot="{ Component }">
          <Transition name="fade" mode="out-in">
            <component :is="Component" />
          </Transition>
        </RouterView>
        <PoweredFooter v-if="!isWidget" />
        <Footer v-if="!isWidget" />
      </MainContent>
      <MainSettingSidebar v-if="!isWidget" />
    </div>
    <MainModal />
    <Snackbar />
  </div>
</template>

<script setup>
import { watch, onMounted, provide, ref, computed } from "vue";
import { useRoute, RouterView } from "vue-router";
import axios from "axios";

import { useGlobalStore } from "@/stores/globalStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import { useNotificationStore } from "@/stores/components/notificationStore";

import useTheme from "@/composables/ui/useTheme.js";
import useThirdPartyScripts from "@/composables/api/useThirdPartyScripts";
import useMerchantSettings from "@/composables/ui/useMerchantSettings";
import useLanguageSnackbar from "@/composables/ui/useLanguageSnackbar";

import PoweredFooter from "@/components/layout/PoweredFooter.vue";
import Footer from "@/components/layout/Footer.vue";
import MainBackground from "@/components/layout/MainBackground.vue";
import NFTMainBackground from "@/components/layout/NftMainBackground.vue";
import MainSidebar from "@/components/layout/MainSidebar.vue";
import MainContent from "@/components/layout/MainContent.vue";
import MainSettingSidebar from "@/components/layout/MainSettingSidebar.vue";
import MainBanner from "@/components/ui/alerts/MainBanner.vue";
import SplashScreen from "@/components/layout/SplashScreen.vue";
import MainModal from "@/components/ui/modals/MainModal.vue";
import Snackbar from "@/components/ui/alerts/Snackbar.vue";

const { useSiftScript } = useThirdPartyScripts();

const { updateThemeFromQuery, updateAppTheme } = useTheme();
const { getMerchantSettings, getNotifications } = useMerchantSettings();
const { detectedCountryCode, handleGeolocationSnackbar } =
  useLanguageSnackbar();
const globalStore = useGlobalStore();
const orderStore = useOrderFormStore();
const notificationStore = useNotificationStore();
const route = useRoute();

const provideUiDisable = ref(false);

const isWidget = computed(() => route?.name && route.name.includes("widget"));
const showBanner = computed(
  () =>
    route?.name &&
    ((route.name.includes("order") &&
      notificationStore.getBannerData.order?.toShow) ||
      (route.name.includes("summary") &&
        notificationStore.getBannerData.orderStatus?.toShow))
);

const getNotificationBanner = computed(() => {
  if (route?.name && route.name.includes("order")) {
    return notificationStore.getBannerData.order;
  }

  if (route?.name && route.name.includes("summary")) {
    return notificationStore.getBannerData.orderStatus;
  }

  return {};
});

provide("isDisabledByKey", provideUiDisable);

const bindGeoToStore = (resp) => {
  globalStore.setDetectedGeo(resp.data);
};

const fetchUsersGeo = () => {
  axios.get("https://get.geojs.io/v1/ip/geo.json").then((resp) => {
    bindGeoToStore(resp);
  });
};

provide("isDisabledByKey", provideUiDisable);

const tempRemoveConsumerReportStorage = () => {
  // Josh A:
  // Adding local storage removal to prevent the local storage persisting when we've removed the requirement
  // TODO: Find a way to properly handle this situation so we don't encounter in the future.
  const tempVerifObj = JSON.parse(localStorage.getItem("verification"));
  if (tempVerifObj) {
    delete tempVerifObj.consumerReport;
    localStorage.setItem("verification", JSON.stringify(tempVerifObj));
  }
};

onMounted(async () => {
  // TODO: Remove at later date
  tempRemoveConsumerReportStorage();

  await Promise.allSettled([
    useSiftScript(),
    getMerchantSettings(),
    getNotifications(),
  ]);
  updateAppTheme(route.query);
  fetchUsersGeo();
});

watch(
  () => route.query.theme,
  (newTheme) => {
    if (!newTheme) {
      return;
    }

    updateThemeFromQuery(newTheme);
  },
  { deep: true }
);

watch(
  () => route.name,
  () => {
    if (!route.name) return;
    if (route.name.includes("nft")) {
      orderStore.setActiveMode("NFT-BUY");
    }
  },
  { immediate: true }
);

watch(
  () => route,
  (val) => {
    if (val && route.name === "/order") {
      localStorage.removeItem("token");
    }
  },
  { immediate: true }
);

// to allow OrderView to be mounted
setTimeout(() => {
  watch(
    () => detectedCountryCode && !globalStore.global.isShowSplash,
    (toTrigger) => {
      if (!toTrigger) {
        return;
      }

      handleGeolocationSnackbar();
    }
  );
}, 1000);

const centerLayoutClass = computed(() =>
  globalStore.getIsGreyLabel || orderStore.isNFTMode ? "center-layout" : ""
);
</script>

<style lang="scss">
.app {
  width: 100%;
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  @include breakpoint($min-m) {
    height: 100%;
    flex-direction: row;
    overflow: hidden;
  }
}

.app.center-layout {
  width: 100%;
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
